import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Sparkles, Sun, FileText, MapPin, Calculator, Wallet, Scale, Mail,
  ShieldCheck, Check, ArrowRight, Star, BookOpen,
} from "lucide-react";
import { apiClient } from "../lib/api";
import { getHomeT } from "../i18n/home_strings";
import LanguageSwitcher from "../components/LanguageSwitcher";

/* MockupHome — DoctorSol homepage met Summer Sale framing. Mounted op `/` en `/v3`. */

export default function MockupHome() {
  const { i18n } = useTranslation();
  const T = getHomeT(i18n.language);

  // We don't render the counter anymore (user request), but we still ping the
  // endpoint so the backend continues to surface availability if we change our
  // minds later.
  useEffect(() => { apiClient.get("/free-counter").catch(() => {}); }, []);

  const deliverables = [
    { icon: MapPin,     title: T.d1_t, body: T.d1_b },
    { icon: Sun,        title: T.d2_t, body: T.d2_b },
    { icon: Calculator, title: T.d3_t, body: T.d3_b },
    { icon: Wallet,     title: T.d4_t, body: T.d4_b },
    { icon: Scale,      title: T.d5_t, body: T.d5_b },
    { icon: BookOpen,   title: T.d6_t, body: T.d6_b },
    { icon: Sparkles,   title: T.d7_t, body: T.d7_b },
  ];

  return (
    <div className="min-h-screen bg-[#F8F6F0] text-[#0F172A]"
         style={{ fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Inter', 'Helvetica Neue', Arial, sans-serif", WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" }}>
      <style>{`
        .serif { font-family: inherit; letter-spacing: -0.035em; font-weight: 600; }
        h1, h2, h3 { letter-spacing: -0.035em; font-weight: 600; }
        body { -webkit-font-smoothing: antialiased; }
        @keyframes pulse-dot { 0%, 100% { opacity: 1; transform: scale(1); } 50% { opacity: .55; transform: scale(1.35); } }
        .pulse-dot { animation: pulse-dot 1.6s ease-in-out infinite; }
        @keyframes floaty { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        .floaty { animation: floaty 6s ease-in-out infinite; }
      `}</style>

      {/* Top nav */}
      <header className="px-6 md:px-12 lg:px-24 py-6 flex items-center justify-between border-b border-[#0F172A]/5">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#E78938] flex items-center justify-center">
            <Sun className="w-5 h-5 text-white" />
          </div>
          <span className="serif text-xl font-medium">DoctorSol</span>
        </div>
        <div className="hidden md:flex items-center gap-8 text-sm text-[#475569]">
          <a href="#wat-krijg-je">{T.nav_what}</a>
          <a href="#hoe-werkt-het">{T.nav_how}</a>
          <a href="#waarom-gratis">{T.nav_about}</a>
          <LanguageSwitcher />
          <Link to="/intake" data-testid="nav-start"
            className="bg-[#0F172A] text-white px-5 py-2.5 rounded-full text-sm hover:bg-[#E78938] transition-colors">
            {T.nav_start_btn}
          </Link>
        </div>
        <div className="md:hidden">
          <LanguageSwitcher variant="minimal" />
        </div>
      </header>

      {/* Hero — split layout */}
      <section className="px-6 md:px-12 lg:px-24 pt-16 md:pt-24 pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left — typography + CTA */}
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-[#E78938]/10 text-[#E78938] px-4 py-2 rounded-full text-sm font-semibold border border-[#E78938]/20 mb-8">
              <span className="w-2 h-2 bg-[#E78938] rounded-full pulse-dot" />
              <span className="font-bold">Summer&nbsp;Sale</span> · {T.hero_badge_text}
            </div>

            <h1 className="serif text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95] mb-6">
              {T.hero_t_l1}<br/>
              {T.hero_t_l2_pre}<span className="text-[#E78938]">{T.hero_t_highlight}</span>{T.hero_t_l2_post}
            </h1>

            <p className="text-lg text-[#475569] leading-relaxed max-w-xl mb-8">
              {T.hero_p}
            </p>

            <div className="flex flex-wrap items-center gap-4 mb-6">
              <Link to="/intake" data-testid="hero-start-btn"
                className="bg-[#E78938] text-white rounded-full px-8 py-4 font-medium flex items-center gap-2 hover:bg-[#D17627] hover:shadow-[0_8px_32px_rgba(231,137,56,0.3)] transition-all">
                {T.hero_cta} <ArrowRight className="w-4 h-4" />
              </Link>
              <div className="flex items-center gap-3">
                <span className="line-through text-[#94A3B8] text-lg">{T.hero_price_orig}</span>
                <span className="serif text-3xl font-medium text-[#0F172A]">{T.hero_price_now}</span>
                <span className="text-sm text-[#475569]">{T.hero_price_tag}</span>
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-[#475569]">
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22C55E]" /> {T.hero_chk_1}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22C55E]" /> {T.hero_chk_2}</span>
              <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-[#22C55E]" /> {T.hero_chk_3}</span>
            </div>
          </div>

          {/* Right — CSS stacked PDF mockup */}
          <div className="lg:col-span-5 relative h-[480px] hidden lg:block">
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="absolute w-72 h-72 bg-[#E78938]/20 rounded-full blur-3xl"></div>
              <div className="relative w-72 h-96">
                <div className="absolute top-6 left-10 w-64 h-80 bg-white rounded-2xl shadow-xl border border-[#0F172A]/5 rotate-6 opacity-70"></div>
                <div className="absolute top-3 left-5 w-64 h-80 bg-white rounded-2xl shadow-xl border border-[#0F172A]/5 rotate-3 opacity-85"></div>
                <div className="absolute top-0 left-0 w-64 h-80 bg-white rounded-2xl shadow-2xl border border-[#0F172A]/5 floaty overflow-hidden">
                  <div className="h-2 bg-[#E78938]"></div>
                  <div className="p-6">
                    <div className="text-[10px] uppercase tracking-widest text-[#E78938] font-semibold mb-2">{T.pdf_kicker}</div>
                    <div className="serif text-2xl leading-tight mb-4">{T.pdf_title_l1}<br/>{T.pdf_title_l2}</div>
                    <div className="w-full h-28 bg-gradient-to-br from-[#1E293B] to-[#0F172A] rounded-lg mb-4 flex items-center justify-center">
                      <MapPin className="w-10 h-10 text-[#E78938]/80" />
                    </div>
                    <div className="space-y-2">
                      <div className="flex justify-between text-xs"><span className="text-[#475569]">{T.pdf_consumption}</span><span className="font-semibold">5.420 kWh</span></div>
                      <div className="flex justify-between text-xs"><span className="text-[#475569]">{T.pdf_system}</span><span className="font-semibold">6,5 kWp + 10 kWh</span></div>
                      <div className="flex justify-between text-xs"><span className="text-[#475569]">{T.pdf_payback}</span><span className="font-semibold text-[#22C55E]">5,8 {i18n.language === "en" ? "yrs" : i18n.language === "ru" ? "лет" : "yrs"}</span></div>
                      <div className="flex justify-between text-xs"><span className="text-[#475569]">{T.pdf_profit}</span><span className="font-semibold text-[#E78938]">€ 38.420</span></div>
                    </div>
                  </div>
                </div>

                <div className="absolute -bottom-4 -right-4 bg-white rounded-2xl shadow-xl border border-[#0F172A]/5 p-3 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#E78938]" />
                  <div className="text-xs">
                    <div className="font-semibold">{T.pdf_badge_t}</div>
                    <div className="text-[#94A3B8]">{T.pdf_badge_s}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust strip */}
      <div className="border-y border-[#0F172A]/10 bg-white/40">
        <div className="px-6 md:px-12 lg:px-24 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-2 text-sm text-[#475569] tracking-wide">
          <span className="flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#E78938]" /> {T.trust_independent}</span>
          <span className="text-[#0F172A]/20">·</span>
          <span>{T.trust_no_sale}</span>
          <span className="text-[#0F172A]/20">·</span>
          <span>{T.trust_no_oblig}</span>
          <span className="text-[#0F172A]/20">·</span>
          <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-[#E78938] text-[#E78938]" /> {T.trust_rating}</span>
        </div>
      </div>

      {/* Deliverables — bento */}
      <section id="wat-krijg-je" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <div className="text-sm uppercase tracking-widest text-[#E78938] font-semibold mb-4">{T.del_kicker}</div>
          <h2 className="serif text-4xl sm:text-5xl tracking-tight leading-tight">
            {T.del_h2_l1}<br/>{T.del_h2_l2}
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-6 lg:grid-cols-12 gap-5">
          {/* Big card — satellite */}
          <div className="md:col-span-6 lg:col-span-7 bg-white border border-[#0F172A]/5 rounded-2xl overflow-hidden hover:shadow-[0_8px_32px_rgba(15,23,42,0.06)] transition-shadow grid grid-cols-1 md:grid-cols-5">
            <div className="md:col-span-3 p-8 md:p-10 flex flex-col justify-center">
              <MapPin className="w-7 h-7 text-[#E78938] mb-5" />
              <h3 className="serif text-2xl mb-3">{deliverables[0].title}</h3>
              <p className="text-[#475569] leading-relaxed">{deliverables[0].body}</p>
            </div>
            <div className="md:col-span-2 min-h-[200px] md:min-h-0 relative bg-cover bg-center"
                 style={{ backgroundImage: "url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?crop=entropy&cs=srgb&fm=jpg&q=85&w=800')" }}>
              <div className="absolute right-4 bottom-4 bg-white/95 backdrop-blur rounded-xl px-3 py-2 text-xs font-mono">
                {T.d1_map_label}
              </div>
            </div>
          </div>

          {/* Tall card — system design */}
          <div className="md:col-span-6 lg:col-span-5 bg-[#0F172A] text-white rounded-2xl p-8 md:p-10 hover:shadow-[0_8px_32px_rgba(15,23,42,0.15)] transition-shadow">
            <Sun className="w-7 h-7 text-[#E78938] mb-5" />
            <h3 className="serif text-2xl mb-3">{deliverables[1].title}</h3>
            <p className="text-white/70 leading-relaxed mb-6">{deliverables[1].body}</p>
            <div className="grid grid-cols-3 gap-3 pt-6 border-t border-white/10">
              <Spec label={T.spec_panels} value="13×" />
              <Spec label={T.spec_inverter} value="6 kW" />
              <Spec label={T.spec_battery} value="10 kWh" />
            </div>
          </div>

          {/* 4 medium cards */}
          {deliverables.slice(2, 6).map((d) => (
            <div key={d.title} className="md:col-span-3 lg:col-span-3 bg-white border border-[#0F172A]/5 rounded-2xl p-7 hover:shadow-[0_8px_32px_rgba(15,23,42,0.04)] transition-shadow">
              <d.icon className="w-6 h-6 text-[#E78938] mb-4" />
              <h3 className="serif text-lg font-medium mb-2">{d.title}</h3>
              <p className="text-sm text-[#475569] leading-relaxed">{d.body}</p>
            </div>
          ))}

          {/* Wide bottom card — second opinion */}
          <div className="md:col-span-6 lg:col-span-12 bg-gradient-to-r from-[#E78938] to-[#D17627] text-white rounded-2xl p-8 md:p-10 flex flex-col md:flex-row md:items-center gap-6">
            <Sparkles className="w-10 h-10 shrink-0" />
            <div className="flex-1">
              <h3 className="serif text-2xl mb-2">{deliverables[6].title}</h3>
              <p className="text-white/85 leading-relaxed max-w-2xl">{deliverables[6].body}</p>
            </div>
            <Link to="/intake" data-testid="bento-cta"
              className="shrink-0 bg-white text-[#0F172A] rounded-full px-6 py-3 font-medium hover:bg-[#F8F6F0] transition-colors flex items-center gap-2">
              {T.d7_cta} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* Why free section */}
      <section id="waarom-gratis" className="px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="text-sm uppercase tracking-widest text-[#E78938] font-semibold mb-4">{T.free_kicker}</div>
            <h2 className="text-4xl sm:text-5xl tracking-tight leading-tight" style={{ letterSpacing: "-0.035em" }}>
              {T.free_h2_l1}<br/>{T.free_h2_l2}
            </h2>
          </div>
          <div className="lg:col-span-7 space-y-6">
            <p className="text-lg text-[#0F172A] leading-relaxed"
               dangerouslySetInnerHTML={{ __html: T.free_p_html }} />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 pt-4">
              <div className="border-l-2 border-[#E78938] pl-5">
                <div className="font-semibold mb-1">{T.free_b1_t}</div>
                <div className="text-sm text-[#475569] leading-relaxed">{T.free_b1_b}</div>
              </div>
              <div className="border-l-2 border-[#E78938] pl-5">
                <div className="font-semibold mb-1">{T.free_b2_t}</div>
                <div className="text-sm text-[#475569] leading-relaxed">{T.free_b2_b}</div>
              </div>
              <div className="border-l-2 border-[#E78938] pl-5 md:col-span-2">
                <div className="font-semibold mb-1">{T.free_b3_t}</div>
                <div className="text-sm text-[#475569] leading-relaxed">{T.free_b3_b}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section id="hoe-werkt-het" className="bg-white border-y border-[#0F172A]/5 px-6 md:px-12 lg:px-24 py-24 md:py-32">
        <div className="max-w-3xl mb-16">
          <div className="text-sm uppercase tracking-widest text-[#E78938] font-semibold mb-4">{T.how_kicker}</div>
          <h2 className="serif text-4xl sm:text-5xl tracking-tight leading-tight">{T.how_h2}</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {[
            { num: "01", title: T.st1_t, body: T.st1_b },
            { num: "02", title: T.st2_t, body: T.st2_b },
            { num: "03", title: T.st3_t, body: T.st3_b },
          ].map((s) => (
            <div key={s.num} className="border-t border-[#E78938]/30 pt-6">
              <div className="serif text-6xl text-[#E78938]/30 mb-4">{s.num}</div>
              <h3 className="serif text-xl mb-2">{s.title}</h3>
              <p className="text-sm text-[#475569] leading-relaxed">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section className="px-6 md:px-12 lg:px-24 py-24 md:py-32 text-center">
        <div className="inline-flex items-center gap-2 bg-[#E78938]/10 text-[#E78938] px-4 py-2 rounded-full text-sm font-semibold border border-[#E78938]/20 mb-8">
          <span className="w-2 h-2 bg-[#E78938] rounded-full pulse-dot" />
          <span className="font-bold">Summer&nbsp;Sale</span> · {T.hero_badge_text}
        </div>
        <h2 className="serif text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-none mb-6 max-w-3xl mx-auto">
          {T.cta_h2}
        </h2>
        <p className="text-lg text-[#475569] max-w-xl mx-auto mb-10"
           dangerouslySetInnerHTML={{ __html: T.cta_p_html }} />
        <Link to="/intake" data-testid="final-cta"
          className="inline-flex items-center gap-2 bg-[#E78938] text-white rounded-full px-10 py-5 text-lg font-medium hover:bg-[#D17627] hover:shadow-[0_8px_32px_rgba(231,137,56,0.3)] transition-all">
          {T.cta_btn} <ArrowRight className="w-5 h-5" />
        </Link>
      </section>

      <footer className="border-t border-[#0F172A]/10 px-6 md:px-12 lg:px-24 py-10 text-sm text-[#475569] flex flex-wrap justify-between gap-4">
        <div>{T.ft_copy}</div>
        <div className="flex gap-6">
          <a href="#">{T.ft_privacy}</a><a href="#">{T.ft_terms}</a><a href="mailto:info@doctorsol.es">{T.ft_contact}</a>
        </div>
      </footer>
    </div>
  );
}

const Spec = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-widest text-white/50 mb-1">{label}</div>
    <div className="serif text-xl text-[#E78938]">{value}</div>
  </div>
);
